import { SubmitOrderEvent } from '@/services/analytics/events/';
import { AnalyticsEventType, Commerce } from '@/services/analytics/platforms/adobe-analytics/models';
import { BagPickupLocation, Order } from '@/models';
import { CountryCode } from '@/store/ui';
import moment from 'moment';
import { getGroupOrderParticipantCount } from '@/services/order/order-helper';
import { Store } from 'vuex';
import { AppState } from '@/store';
import { ServicesContainer } from '@/services';
import { AdobeBaseEvent } from './adobe-base-event';
import { ProductListItem } from '../classes/product-list-item';
import { addLeadingZero } from '@/helpers';
import { PageParameters } from '@/services/analytics/models';
import { TargetDecisionScope } from '@/models/adobe-target';
import { ORDER_CONFIRMATION_DECISION_SCOPES } from '../constants';
import { buildTargetPageParameters } from '../helpers';

export class AdobeSubmitOrderEvent extends AdobeBaseEvent {
  event = AnalyticsEventType.SubmitOrder;
  commerce: Commerce = {
    ...this.commerce,
    purchases: { value: 1 },
    orderReviewTime: undefined // Ensures compatibility with the Commerce type
  };
  targetPageParameters?: PageParameters;
  targetDecisionScope?: TargetDecisionScope[];

  constructor(event: SubmitOrderEvent, store: Store<AppState>, services: ServicesContainer) {
    super(store, services);

    if (event.order.order.meals) {
      this.productListItems = ProductListItem.getProductListItems(event.order.order.meals, store, services.order.isDelivery());
    }

    const order = event.order;
    const state = store.state;
    const payments = state.order.lastOrder.order?.order.payments;
    const paymentType = payments?.map(p => p.paymentTypeName).reverse().join(', ') || '';
    const customer = state.customer.profile;
    const selectedRestaurant = state.restaurant.selectedRestaurant;
    const pickupTimeDetails = store.state.checkout.pickup;

    this.web = {
      webPageDetails: {
        pageViews: {value: 1},
        name: "order:order-confirmation:home",
        siteSection: "order"
      }
    }

    this.commerce.order = {
      currencyCode: state.ui.countryProfile.countryCode === CountryCode.US ? 'USD' : 'CAD',
      deliveryFeeAmount: order.order.delivery?.feeAmount || 0,
      donationAmount: order.order.orderDonationAmount,
      purchaseID: order.order.orderId,
      orderID: order.order.orderId,
      paymentMethod: paymentType,
      pickupTime: `${moment(order.order.pickupDateTimeLocal).format('h:mma')}|${moment(order.order.pickupDateTimeLocal).diff(moment(), 'minutes')}min`, // Formatting example: 5:00pm|45min
      priceBagTotalAmount: order.order.orderMealsUnitPrice,
      pricePromoAmount: order.order.orderDiscountAmount,
      priceSubTotal: order.order.orderMealsSubTotalAmount,
      priceTotal: order.order.orderTotalAmount,
      regulatoryFeeAmount: order.order.taxesAndFees?.deliveryRegulatoryFee || 0,
      serviceFeeAmount: order.order.taxesAndFees?.deliveryServiceFee || 0,
      taxAmount: order.order.orderTaxAmount,
    };

    const selectedPickupTime = pickupTimeDetails.pickupTime.value;
    if (services.order.isPickingUp() && selectedPickupTime) {
      this.commerce.sputLinkTime = moment(selectedPickupTime).format('h:mma');
      const sputLinkPosition = pickupTimeDetails.availablePickupTimes.indexOf(selectedPickupTime || '');
      if (sputLinkPosition >= 0) {
        this.commerce.sputLinkPosition = addLeadingZero(sputLinkPosition);
      }
    }

    if (order.order.discounts?.length) {
      this.commerce.order.promoCode = order.order.discounts[0].discountCode;
    }

    if (order.order.groupOrderId && order.order.meals) {
      this.commerce.order.groupOrderParticipants = getGroupOrderParticipantCount(order.order.meals) + 1; // need to add 1 since getGroupOrderParticipantCount does not include the host
    }

    if (services.order.isDelivery()) {
      this.commerce.order.driverTipAmount = order.order.orderTipAmount;
      this.commerce.driverTipCount = { value: order.order.orderTipAmount === 0.00 ? 0 : 1 };
    } else if (selectedRestaurant?.crewTipPickupEnabled) { // Only add these values to the data layer if the customer saw crew tipping
      this.commerce.order.crewTipAmount = order.order.orderCrewTipAmount;
      this.commerce.crewTipCount = { value: order.order.orderCrewTipAmount === 0.00 ? 0 : 1 };
    }

    if (order.order.donations && order.order.donations.length > 0) {
      this.commerce.donationCount = { value: 1 }
    }

    if (order.discounts && order.discounts.length) {
      this.commerce.order.promoCode = order.discounts[0].promotionCode;
    }

    // Distinguish rewards from generic promo codes: a discount is a reward only when its code
    // matches a redeemed reward's promotionCode (rewards are applied to the order via that code).
    // A plain promo code has no matching reward and is tracked solely via commerce.order.promoCode.
    const orderDiscountCode = (order.discounts && order.discounts.length)
      ? order.discounts[0].promotionCode
      : order.order.discounts?.[0]?.discountCode;
    const redeemedRewards = event.redeemedRewards ?? [...(state.reward.bagRewards ?? []), ...(state.reward.appliedRewards ?? [])];
    const redeemedReward = orderDiscountCode && redeemedRewards
      ? redeemedRewards.find(reward => reward.promotionCode === orderDiscountCode)
      : undefined;

    if (redeemedReward) {
      this.account.rewardsRedeemed = { value: 1 };
      this.account.rewardsID = orderDiscountCode;
      this.account.rewardsTitle = redeemedReward.title;
      this.account.rewardsType = redeemedReward.offerType; //TODO: get from Sparkfly campaign field
      this.account.rewardsPath = 'rewards-bag';
    } else if (orderDiscountCode) {
      // Option C: For flows like one-tap-reorder the reward is no longer present in
      // bagRewards/appliedRewards at submit time, but the earlier 'rewards-added-to-bag'
      // interaction already persisted the reward fields into the Adobe data layer. Reuse those
      // persisted values, but ONLY when the persisted rewardsID matches this order's actual
      // discount code so stale reward data from a previous order can never leak into the
      // purchases event.
      const persistedAccount = window.adobeDataLayer?.getState?.()?.account;
      const usedPersisted = !!persistedAccount && persistedAccount.rewardsID === orderDiscountCode;
      if (usedPersisted) {
        this.account.rewardsRedeemed = { value: 1 };
        this.account.rewardsID = persistedAccount.rewardsID;
        this.account.rewardsTitle = persistedAccount.rewardsTitle;
        this.account.rewardsType = persistedAccount.rewardsType;
        this.account.rewardsPath = persistedAccount.rewardsPath ?? 'rewards-bag';
      }
      // Single troubleshooting log: shows whether the persisted data-layer reward fields were
      // reused (Option C) or omitted, and what the data layer actually carried for this order.
      services.logger.info('[Adobe SubmitOrder] reward fields from data layer', {
        orderDiscountCode,
        usedPersisted,
        persistedRewardsID: persistedAccount?.rewardsID,
        persistedRewardsTitle: persistedAccount?.rewardsTitle,
        persistedRewardsType: persistedAccount?.rewardsType,
        persistedRewardsPath: persistedAccount?.rewardsPath,
      });
    }

    this.commerce.checkoutType = customer?.isGuest ? 'guest' : services.loyalty.isEnrolledInLoyalty(customer) ? 'loyalty' : 'non-loyalty';
    this.commerce.retrievalType = this.getRetrievalType(event?.order?.order);

    if (state.order.upsellMeal) {
      this.commerce.upsellOrder = { value: 1 };
    }

    this.targetPageParameters = buildTargetPageParameters(store, services, event.viewName);
    this.targetDecisionScope = ORDER_CONFIRMATION_DECISION_SCOPES;
  }

  getRetrievalType(order: Order): string {
    if (order.delivery) {
      return 'Delivery';
    }

    const bagPickupLocation = order?.bagPickupLocationId;

    // Default to 'Pickup_Inside' if order is not 'Walk_Up' nor 'Chipotlane'
    return bagPickupLocation == BagPickupLocation.WalkUpWindow ? 'Walk_Up' :
      bagPickupLocation == BagPickupLocation.Chipotlane ? 'Chipotlane' : 'Pickup_Inside';
  }
}
